/**
 * Idle presence: Away (5m) → On break / System Inactivity (30m) → Long Away (1h) → Offline (6h).
 * Restores Available when the user comes back (unless attendance locks status or status was set manually).
 */
import { store } from '../store';
import { setUserStatus, updateUserStatus } from '../store/slices/authSlice';
import { endShortBreak, startShortBreak } from './attendanceService';
import { notifyAttendanceSessionChanged } from './attendanceSessionBus';
import tabSessionService from './tabSessionService';

const AWAY_MS = 5 * 60 * 1000;
const SYSTEM_INACTIVITY_MS = 30 * 60 * 1000;
const LONG_AWAY_MS = 60 * 60 * 1000;
const OFFLINE_MS = 6 * 60 * 60 * 1000;
const TODAY_TASKS_IDLE_MS = 15 * 60 * 1000;
const CHECK_EVERY_MS = 30 * 1000;
const PENDING_TODAY_TASKS_KEY = 'workloop.pendingTodayTasks';
export const SYSTEM_INACTIVITY_REASON = 'System Inactivity';

const ATTENDANCE_LOCKED = new Set(['LOGGED_OUT', 'ON_LEAVE', 'SICK_LEAVE', 'HOLIDAY', 'DO_NOT_DISTURB', 'HAVING_FOOD', 'IN_A_CALL']);
const IDLE_STATUSES = new Set(['AVAILABLE', 'ONLINE', 'AWAY', 'LONG_AWAY', 'OFFLINE']);

let started = false;
let lastActivityAt = Date.now();
let hiddenAt = null;
let lastApplied = null;
let todayTasksOpened = false;
let checkTimer = null;
let inactivityBreakActive = false;
let inactivityBreakBusy = false;
let inactivityBreakSkipped = false;

const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown', 'wheel'];

const getAuthUser = () => store.getState()?.auth?.user || null;

const isLocked = (user) => {
  if (!user) return true;
  // User knowingly set a status — never overwrite with idle Away / Long Away / Offline.
  if (user.statusSource === 'MANUAL') return true;
  if (ATTENDANCE_LOCKED.has(user.status)) return true;
  if (user.statusSource === 'ATTENDANCE' && user.status === 'AWAY') return true;
  return false;
};

const onCallPage = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  const hash = window.location.hash || '';
  return path.startsWith('/call') || hash.startsWith('#/call');
};

const openTodayTasks = () => {
  if (todayTasksOpened || onCallPage()) return;
  if (!getAuthUser()) return;
  if (currentIdleMs() < TODAY_TASKS_IDLE_MS) return;
  todayTasksOpened = true;
  try {
    sessionStorage.setItem(PENDING_TODAY_TASKS_KEY, '1');
    window.__workloopFocusTodayTasks = true;
  } catch {
    window.__workloopFocusTodayTasks = true;
  }
  window.dispatchEvent(new CustomEvent('workloop:open-daily-tasks', {
    detail: { focusTasks: true, extended: true },
  }));
};

const isSystemInactivityBreak = (user) => {
  if (inactivityBreakActive) return true;
  if (!user?.onBreak) return false;
  return String(user.statusMessage || '').trim() === SYSTEM_INACTIVITY_REASON;
};

const applyLocalBreakPresence = (onBreak) => {
  store.dispatch(setUserStatus({
    status: onBreak ? 'AWAY' : 'AVAILABLE',
    statusMessage: onBreak ? SYSTEM_INACTIVITY_REASON : null,
    statusSource: 'ATTENDANCE',
    onBreak,
  }));
};

const startSystemInactivityBreak = () => {
  if (inactivityBreakBusy || inactivityBreakActive || inactivityBreakSkipped) return;
  if (onCallPage()) return;
  const user = getAuthUser();
  if (!user || isLocked(user)) return;
  if (ATTENDANCE_LOCKED.has(user.status) || user.onBreak) return;
  if (!tabSessionService.isActiveTab()) return;

  inactivityBreakBusy = true;
  startShortBreak(SYSTEM_INACTIVITY_REASON)
    .then((result) => {
      inactivityBreakActive = true;
      lastApplied = 'INACTIVITY_BREAK';
      applyLocalBreakPresence(true);
      notifyAttendanceSessionChanged({ status: result?.status || null });
    })
    .catch(() => {
      inactivityBreakSkipped = true;
    })
    .finally(() => {
      inactivityBreakBusy = false;
    });
};

const endSystemInactivityBreak = () => {
  const user = getAuthUser();
  if (!isSystemInactivityBreak(user)) return;
  if (inactivityBreakBusy) return;
  if (!tabSessionService.isActiveTab()) return;

  inactivityBreakBusy = true;
  endShortBreak()
    .then((result) => {
      inactivityBreakActive = false;
      lastApplied = 'AVAILABLE';
      applyLocalBreakPresence(false);
      notifyAttendanceSessionChanged({ status: result?.status || null });
    })
    .catch(() => {
      inactivityBreakActive = false;
    })
    .finally(() => {
      inactivityBreakBusy = false;
    });
};

const markActivity = () => {
  lastActivityAt = Date.now();
  todayTasksOpened = false;
  inactivityBreakSkipped = false;
  const user = getAuthUser();
  if (!user) return;
  if (!tabSessionService.isActiveTab()) return;

  if (isSystemInactivityBreak(user)) {
    endSystemInactivityBreak();
    return;
  }

  if (isLocked(user)) return;

  if (user.statusSource === 'IDLE' || ['AWAY', 'LONG_AWAY', 'OFFLINE'].includes(user.status)) {
    if (lastApplied !== 'AVAILABLE') {
      lastApplied = 'AVAILABLE';
      store.dispatch(updateUserStatus({ status: 'AVAILABLE', source: 'IDLE' }));
    }
  }
};

const desiredIdleStatus = (idleMs) => {
  if (idleMs >= OFFLINE_MS) return 'OFFLINE';
  if (idleMs >= LONG_AWAY_MS) return 'LONG_AWAY';
  if (idleMs >= AWAY_MS) return 'AWAY';
  return null;
};

const currentIdleMs = () => {
  const sinceActivity = Date.now() - lastActivityAt;
  const sinceHidden = hiddenAt != null ? Date.now() - hiddenAt : 0;
  return Math.max(sinceActivity, sinceHidden);
};

const tick = () => {
  if (!tabSessionService.isActiveTab()) return;
  if (currentIdleMs() >= TODAY_TASKS_IDLE_MS) {
    openTodayTasks();
  }
  const user = getAuthUser();
  if (!user) return;
  if (isSystemInactivityBreak(user)) return;

  const idleMs = currentIdleMs();
  if (idleMs >= SYSTEM_INACTIVITY_MS && !inactivityBreakSkipped) {
    startSystemInactivityBreak();
    return;
  }

  if (isLocked(user)) return;
  if (!IDLE_STATUSES.has(user.status) && user.statusSource !== 'IDLE') return;

  const next = desiredIdleStatus(idleMs);
  if (!next) return;
  if (user.status === next && user.statusSource === 'IDLE') return;
  if (lastApplied === next) return;

  lastApplied = next;
  store.dispatch(updateUserStatus({ status: next, source: 'IDLE' }));
};

const onVisibility = () => {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
  } else {
    const idleMs = currentIdleMs();
    hiddenAt = null;
    if (idleMs >= TODAY_TASKS_IDLE_MS) {
      openTodayTasks();
    }
    if (isSystemInactivityBreak(getAuthUser())) {
      endSystemInactivityBreak();
    }
    lastActivityAt = Date.now();
  }
};

export const startPresenceIdleWatcher = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  lastActivityAt = Date.now();
  hiddenAt = document.visibilityState === 'hidden' ? Date.now() : null;
  lastApplied = null;
  inactivityBreakActive = false;
  inactivityBreakBusy = false;
  inactivityBreakSkipped = false;

  activityEvents.forEach((evt) => {
    window.addEventListener(evt, markActivity, { passive: true });
  });
  document.addEventListener('visibilitychange', onVisibility);
  checkTimer = window.setInterval(tick, CHECK_EVERY_MS);
};

export const stopPresenceIdleWatcher = () => {
  if (!started) return;
  started = false;
  activityEvents.forEach((evt) => {
    window.removeEventListener(evt, markActivity);
  });
  document.removeEventListener('visibilitychange', onVisibility);
  if (checkTimer) {
    window.clearInterval(checkTimer);
    checkTimer = null;
  }
  lastApplied = null;
  inactivityBreakActive = false;
  inactivityBreakBusy = false;
  inactivityBreakSkipped = false;
};

export default { startPresenceIdleWatcher, stopPresenceIdleWatcher };
